// COLLECTOR-CORRECTION-PREMIUM-UI-001 — shared centered premium modal shell for Collector Correction review (presentation only).
import React, { useState } from 'react';
import { Landmark, ClipboardList, Flag, Clock3, FileText, UserRound, CalendarDays, Phone, MapPin, ChevronDown } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import '../../pages/ccr-premium.css';

export const CCR_ISSUE_TYPE_LABELS = {
  missing_collector: 'Missing collector', incorrect_name: 'Incorrect name', incorrect_address: 'Incorrect address',
  incorrect_phone: 'Incorrect phone', wrong_collector_entirely: 'Wrong collector entirely', other: 'Other',
};
const PRIORITY = { high: ['bg-red-500', 'High'], medium: ['bg-amber-400', 'Medium'], low: ['bg-emerald-500', 'Low'] };
const STATUS_TONE = { pending: 'bg-amber-100 text-amber-800', approved: 'bg-emerald-100 text-emerald-800', rejected: 'bg-rose-100 text-rose-800', duplicate: 'bg-slate-200 text-slate-700' };

export const fmtDateET = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' ET'; }
  catch { return '—'; }
};

const StatusChip = ({ status }) => (
  <span className={`ccr-chip ${STATUS_TONE[status] || 'bg-slate-100 text-slate-700'}`} data-testid="ccr-modal-status">{status === 'pending' ? 'Pending Review' : status}</span>
);
const Priority = ({ bucket }) => { const [dot, label] = PRIORITY[bucket] || PRIORITY.low; return <span className="inline-flex items-center gap-1.5" data-testid={`priority-${bucket || 'low'}-pill`}><span className={`h-2 w-2 rounded-full ${dot}`} />{label}</span>; };

export const CollectorCorrectionModal = ({ open, onOpenChange, row, children, footer, testId = 'ccr-drawer', className = '', zIndexClass = '' }) => {
  const [descOpen, setDescOpen] = useState(false);
  if (!row) return null;
  const tiles = [
    { label: 'Issue Type', value: CCR_ISSUE_TYPE_LABELS[row.request_type] || row.request_type, Icon: ClipboardList, tone: 'bg-indigo-50 text-indigo-600' },
    { label: 'Priority', value: <Priority bucket={row.priority_bucket || 'low'} />, Icon: Flag, tone: 'bg-amber-50 text-amber-600' },
    { label: 'Status', value: <span className="capitalize">{row.status}</span>, Icon: Clock3, tone: 'bg-emerald-50 text-emerald-600' },
    { label: 'Affected Orders', value: row.affected_orders_count ?? (row.affected_order_ids || []).length, Icon: FileText, tone: 'bg-violet-50 text-violet-600' },
    { label: 'Submitted By', value: row.submitted_by_name || row.submitted_by_id || '—', Icon: UserRound, tone: 'bg-sky-50 text-sky-600' },
    { label: 'Submitted', value: fmtDateET(row.submitted_at), Icon: CalendarDays, tone: 'bg-rose-50 text-rose-600' },
  ];
  const hasSuggested = row.suggested_collector_name || row.suggested_collector_phone || row.suggested_collector_address;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`ccr-modal ${zIndexClass} ${className}`} data-testid={testId}>
        <DialogHeader className="ccr-head space-y-0 text-left">
          <span className="ccr-head-icon"><Landmark /></span>
          <div className="min-w-0">
            <DialogTitle className="ccr-title flex items-center gap-2.5">Collector Correction <StatusChip status={row.status} /></DialogTitle>
            <DialogDescription className="ccr-sub">{row.jurisdiction?.jurisdiction_name || row.jurisdiction_id} · {row.jurisdiction?.state || '—'} · {row.tax_period}</DialogDescription>
          </div>
        </DialogHeader>
        <div className="ccr-body">
          <div className="ccr-tiles" data-testid="ccr-summary-tiles">
            {tiles.map((t, i) => (
              <div key={t.label} className="ccr-tile" style={{ animationDelay: `${i * 30}ms` }} data-testid={`ccr-tile-${t.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <span className={`ccr-tile-icon ${t.tone}`}><t.Icon /></span>
                <div className="min-w-0"><div className="ccr-tile-label">{t.label}</div><div className="ccr-tile-value">{t.value}</div></div>
              </div>
            ))}
          </div>
          {row.description && (
            <button type="button" className={`ccr-desc text-left ${descOpen ? 'is-open' : ''}`} onClick={() => setDescOpen((v) => !v)} aria-expanded={descOpen} data-testid="ccr-description">
              <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-500" />
              <span className="ccr-desc-label">Description</span>
              <span className="ccr-desc-text">{row.description}</span>
              <ChevronDown className="ccr-desc-toggle h-4 w-4" />
            </button>
          )}
          {hasSuggested && (
            <div className="ccr-suggest" data-testid="ccr-submitted-data">
              <div className="ccr-tile"><span className="ccr-tile-icon bg-white text-indigo-600"><Landmark /></span><div className="min-w-0"><div className="ccr-tile-label">Suggested Collector</div><div className="ccr-tile-value" title={row.suggested_collector_name || ''}>{row.suggested_collector_name || '—'}</div></div></div>
              <div className="ccr-tile"><span className="ccr-tile-icon bg-white text-indigo-600"><Phone /></span><div className="min-w-0"><div className="ccr-tile-label">Phone</div><div className="ccr-tile-value">{row.suggested_collector_phone || '—'}</div></div></div>
              <div className="ccr-tile"><span className="ccr-tile-icon bg-white text-indigo-600"><MapPin /></span><div className="min-w-0"><div className="ccr-tile-label">Address</div><div className="ccr-tile-value" title={row.suggested_collector_address || ''}>{row.suggested_collector_address || '—'}</div></div></div>
            </div>
          )}
          {row.reviewed_at && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-600" data-testid="ccr-review-history">
              <span className="font-semibold text-slate-700">Reviewed by {row.reviewed_by_name || '—'}</span> · {fmtDateET(row.reviewed_at)}
              {(row.review_notes || row.resolution_action) && <div className="mt-0.5">{row.review_notes || row.resolution_action}</div>}
              {row.duplicate_of_id && <div className="mt-0.5">Duplicate of {row.duplicate_of_id}</div>}
            </div>
          )}
          {row.resolution && (
            <section className="ccr-done text-sm" data-testid="ccr-resolution-summary">
              <div className="font-semibold">Resolved · {row.resolution.collector_name}{row.resolution.collector_created ? ' (new collector)' : ''}</div>
              <div className="text-[12px]">Assignment {String(row.resolution.assignment_id || '').slice(0, 8)}{row.resolution.acknowledged_payee_conflict ? ' · payee conflict acknowledged' : ''}</div>
            </section>
          )}
          {children}
          {footer}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CollectorCorrectionModal;
